import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { PoiStatus } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';

/**
 * Default labels for POI status values.
 * These are used when no custom labels are configured at the structure level.
 */
const DEFAULT_POI_STATUS_LABELS: Record<string, string> = {
  [PoiStatus.Inprogress]: 'In Progress',
  [PoiStatus.Completed]: 'Completed',
  [PoiStatus.Inreview]: 'In Review',
  [PoiStatus.ChaseItemAdded]: 'Chase Item Added',
  [PoiStatus.PartiallyCompleted]: 'Partially Completed',
};

/**
 * Hook to get dynamic POI status labels from Settings collection.
 * Falls back to default labels if no custom labels are configured.
 *
 * @returns Object with:
 * - `getPoiStatusLabel`: Function to get the display label for a given status
 * - `labels`: Record mapping each PoiStatus to its display label
 * - `statusOptions`: Array of { value, label } for use in dropdowns
 */
export const usePoiStatusLabels = () => {
  const poiStatusLabels = useAtomValue(state.poiStatusLabels);

  const labels = useMemo(() => {
    const result: Record<string, string> = {};

    // First, add all enum-based default labels
    Object.entries(DEFAULT_POI_STATUS_LABELS).forEach(([key, value]) => {
      result[key] = value;
    });

    // Then, override with custom labels from Settings (and add any new custom statuses)
    if (poiStatusLabels?.meta) {
      for (const metaItem of poiStatusLabels.meta) {
        if (metaItem.__typename === 'StringMeta') {
          result[metaItem.key] = metaItem.value;
        }
      }
    }

    return result;
  }, [poiStatusLabels]);

  const getPoiStatusLabel = useMemo(() => {
    return (status: string): string => {
      return labels[status] ?? status;
    };
  }, [labels]);

  const statusOptions = useMemo(() => {
    // Return all status options from labels (both enum-based and custom from DB)
    return Object.entries(labels).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [labels]);

  return {
    getPoiStatusLabel,
    labels,
    statusOptions,
  };
};

export { DEFAULT_POI_STATUS_LABELS };
